import { useState, useEffect, useRef } from 'react'
import { X, Send, Loader2, Plus, Paperclip, Settings } from 'lucide-react'
import api from '../../core/services/api'

interface Contatto {
  email: string
  label: string
  dettaglio: string
  tipo: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  offertaId: string
  offertaNumero: string
  clienteNome: string
  referenteEmail?: string | null
  onSent: () => void
}

export default function InviaEmailModal({
  isOpen, onClose, offertaId, offertaNumero, clienteNome, referenteEmail, onSent
}: Props) {
  const [to, setTo] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [allegaPdf, setAllegaPdf] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Rubrica
  const [rubricaSearch, setRubricaSearch] = useState('')
  const [rubricaResults, setRubricaResults] = useState<Contatto[]>([])
  const [showRubrica, setShowRubrica] = useState(false)
  const [rubricaTarget, setRubricaTarget] = useState<'to' | 'cc'>('to')
  const rubricaRef = useRef<HTMLDivElement>(null)

  // Config email check
  const [hasConfig, setHasConfig] = useState<boolean | null>(null)
  const [showConfig, setShowConfig] = useState(false)
  const [configForm, setConfigForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    use_tls: true, sender_name: '', sender_email: '', firma_html: '',
  })
  const [savingConfig, setSavingConfig] = useState(false)


  useEffect(() => {
    if (!isOpen) return
    checkConfig()
    // Pre-compila
    setSubject(`Offerta ${offertaNumero} — ${clienteNome}`)
    setBodyHtml(
      `<p>Gentilissimi,</p>` +
      `<p>in allegato trovate la nostra offerta <strong>${offertaNumero}</strong>.</p>` +
      `<p>Restiamo a disposizione per qualsiasi chiarimento.</p>` +
      `<p>Cordiali saluti</p>`
    )
    // Se c'e' referente con email, pre-aggiungi
    if (referenteEmail) {
      setTo([referenteEmail])
    } else {
      setTo([])
    }
    setCc([])
    setError(null)
  }, [isOpen, offertaNumero, clienteNome, referenteEmail])

  const checkConfig = async () => {
    try {
      const res = await api.get('/v1/email/config')
      setHasConfig(!!res.data)
      if (res.data) {
        setConfigForm({
          smtp_host: res.data.smtp_host || '',
          smtp_port: res.data.smtp_port || 587,
          smtp_user: res.data.smtp_user || '',
          smtp_password: '',
          use_tls: res.data.use_tls ?? true,
          sender_name: res.data.sender_name || '',
          sender_email: res.data.sender_email || '',
          firma_html: res.data.firma_html || '',
        })
      }
    } catch {
      setHasConfig(false)
    }
  }

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true)
      await api.put('/v1/email/config', configForm)
      setHasConfig(true)
      setShowConfig(false)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore salvataggio configurazione')
    } finally {
      setSavingConfig(false)
    }
  }

  const searchRubrica = async (query: string) => {
    setRubricaSearch(query)
    if (query.length < 2) { setRubricaResults([]); return }
    try {
      const res = await api.get(`/v1/email/rubrica?search=${encodeURIComponent(query)}`)
      setRubricaResults(res.data)
    } catch {
      setRubricaResults([])
    }
  }

  const addEmail = (email: string, target: 'to' | 'cc') => {
    if (!email || !email.includes('@')) return
    if (target === 'to') {
      if (!to.includes(email)) setTo(prev => [...prev, email])
    } else {
      if (!cc.includes(email)) setCc(prev => [...prev, email])
    }
    setRubricaSearch('')
    setRubricaResults([])
    setShowRubrica(false)
  }

  const removeEmail = (email: string, target: 'to' | 'cc') => {
    if (target === 'to') setTo(prev => prev.filter(e => e !== email))
    else setCc(prev => prev.filter(e => e !== email))
  }

  const handleSend = async () => {
    if (to.length === 0) { setError('Aggiungi almeno un destinatario'); return }
    try {
      setSending(true)
      setError(null)
      await api.post('/v1/email/invia-offerta', {
        offerta_id: offertaId,
        to_addresses: to,
        cc_addresses: cc,
        subject,
        body_html: bodyHtml,
        allega_pdf: allegaPdf,
      })
      onSent()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Errore invio email')
    } finally {
      setSending(false)
    }
  }

  if (!isOpen) return null

  // Mostra form configurazione se non esiste
  if (hasConfig === false || showConfig) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-lg font-bold text-gray-900">Configurazione Email</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              Configura il tuo account email per inviare offerte dalla piattaforma.
              Per Gmail usa una "App Password". Per Outlook 365 usa la tua password.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Server SMTP</label>
                <input
                  type="text" value={configForm.smtp_host}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Porta</label>
                <input
                  type="number" value={configForm.smtp_port}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_port: parseInt(e.target.value) || 587 })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Utente SMTP</label>
                <input
                  type="text" value={configForm.smtp_user}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_user: e.target.value })}
                  placeholder="mario@azienda.it"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password SMTP</label>
                <input
                  type="password" value={configForm.smtp_password}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_password: e.target.value })}
                  placeholder="App password..."
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nome mittente</label>
                <input
                  type="text" value={configForm.sender_name}
                  onChange={(e) => setConfigForm({ ...configForm, sender_name: e.target.value })}
                  placeholder="Mario Rossi"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email mittente</label>
                <input
                  type="email" value={configForm.sender_email}
                  onChange={(e) => setConfigForm({ ...configForm, sender_email: e.target.value })}
                  placeholder="mario@azienda.it"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="use_tls" checked={configForm.use_tls}
                onChange={(e) => setConfigForm({ ...configForm, use_tls: e.target.checked })}
              />
              <label htmlFor="use_tls" className="text-sm text-gray-600">Usa TLS (STARTTLS)</label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Firma HTML (opzionale)</label>
              <textarea
                rows={3} value={configForm.firma_html}
                onChange={(e) => setConfigForm({ ...configForm, firma_html: e.target.value })}
                placeholder="<p>Mario Rossi<br>Azienda Srl<br>Tel: 012 345 6789</p>"
                className="w-full px-3 py-2 border rounded-lg text-sm resize-none font-mono"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <strong>Gmail:</strong> smtp.gmail.com:587 TLS — usa App Password (non la password dell'account)<br />
              <strong>Outlook 365:</strong> smtp.office365.com:587 TLS
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annulla</button>
            <button
              onClick={handleSaveConfig}
              disabled={!configForm.smtp_host || !configForm.smtp_user || !configForm.smtp_password || !configForm.sender_email || savingConfig}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Salva configurazione
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (hasConfig === null) return null // Loading

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Invia Offerta via Email</h3>
            <p className="text-sm text-gray-500">{offertaNumero}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowConfig(true)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400" title="Impostazioni email">
              <Settings className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Destinatari A: */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">A:</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {to.map(email => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                  {email}
                  <button onClick={() => removeEmail(email, 'to')} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="relative" ref={rubricaRef}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={rubricaSearch}
                  onChange={(e) => { searchRubrica(e.target.value); setRubricaTarget('to'); setShowRubrica(true) }}
                  onFocus={() => { setRubricaTarget('to'); setShowRubrica(true) }}
                  placeholder="Cerca nella rubrica o digita email..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={() => addEmail(rubricaSearch, 'to')}
                  disabled={!rubricaSearch.includes('@')}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-30 text-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {showRubrica && rubricaTarget === 'to' && rubricaResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {rubricaResults.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => addEmail(c.email, 'to')}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-sm"
                    >
                      <span className="font-medium">{c.label}</span>
                      <span className="text-gray-400 ml-2">&lt;{c.email}&gt;</span>
                      <span className="text-gray-400 text-xs ml-2">{c.dettaglio}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CC */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CC:</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {cc.map(email => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                  {email}
                  <button onClick={() => removeEmail(email, 'cc')} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="relative">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={rubricaTarget === 'cc' ? rubricaSearch : ''}
                  onChange={(e) => { searchRubrica(e.target.value); setRubricaTarget('cc'); setShowRubrica(true) }}
                  onFocus={() => { setRubricaTarget('cc'); setShowRubrica(true) }}
                  placeholder="Aggiungi CC..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={() => addEmail(rubricaSearch, 'cc')}
                  disabled={!rubricaSearch.includes('@')}
                  className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-30 text-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {showRubrica && rubricaTarget === 'cc' && rubricaResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {rubricaResults.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => addEmail(c.email, 'cc')}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-sm"
                    >
                      <span className="font-medium">{c.label}</span>
                      <span className="text-gray-400 ml-2">&lt;{c.email}&gt;</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Oggetto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto</label>
            <input
              type="text" value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Corpo email</label>
            <textarea
              rows={6} value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Supporta HTML. La firma configurata verra' aggiunta automaticamente.</p>
          </div>

          {/* Allegato */}
          <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-3">
            <Paperclip className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700 flex-1">Allegato: {offertaNumero}</span>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={allegaPdf} onChange={() => setAllegaPdf(true)} />
              PDF
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={!allegaPdf} onChange={() => setAllegaPdf(false)} />
              Word
            </label>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annulla</button>
          <button
            onClick={handleSend}
            disabled={to.length === 0 || !subject || sending}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Invia Email
          </button>
        </div>
      </div>
    </div>
  )
}
